import { Context } from '../..';
import { RequiredEntity, SlugEntity } from '../../../models/view/entity';
import { NotificationPriority, NotificationType } from '../../../notifications/notifications.models';
import * as base from '../base';

// models
export interface PeerAssessmentEntity {
  learnerSlug: string;
  fullName: string;
  roles: SlugEntity[];
}

export interface PeerAssessmentReviewerRequest {
  learnerSlug: string;
  isManager: boolean;
}

export type PeerAssessmentReviewerResponse = PeerAssessmentReviewerRequest & PeerAssessmentEntity;

export interface PeerAssessmentAssigneeRequest {
  learnerSlug: string;
  companySkillIds: number[];
  reviewers: PeerAssessmentReviewerRequest[];
}

export interface PeerAssessmentSkillResponse extends RequiredEntity {
  skillCategories: SlugEntity[];
}

export interface PeerAssessmentAssigneeResponse extends PeerAssessmentEntity {
  department: string | undefined;
  team: string | undefined;
  email: string | undefined;
  reviewers: PeerAssessmentReviewerResponse[];
  companySkills: PeerAssessmentSkillResponse[];
}

export interface PeerAssessmentRequest {
  isSkillsFeedbackEnabled: boolean;
  isAssessmentFeedbackEnabled: boolean;
  isAnonymous: boolean;
  isWeightingEnabled: boolean;
  assignees: PeerAssessmentAssigneeRequest[];
}

export interface PeerAssessmentResponse {
  isSkillsFeedbackEnabled: boolean;
  isAssessmentFeedbackEnabled: boolean;
  isAnonymous: boolean;
  isWeightingEnabled: boolean;
  assignees: PeerAssessmentAssigneeResponse[];
}

export interface PeerAssessmentAssigneeManagersResponse {
  learnerSlug: string;
  managers: PeerAssessmentReviewerResponse[];
}

export interface PeerAssessmentAssigneeRoleSkillsResponse {
  learnerSlug: string;
  roleSkills: PeerAssessmentSkillResponse[];
}

export interface GetPeerAssessmentRequest {
  assessmentSlug: string;
  learnerSlug?: string;
}

const CONTROLLER = `peerendorsement`;

export async function getPeerAssessment({ state, actions }: Context, payload: GetPeerAssessmentRequest): Promise<PeerAssessmentResponse | undefined> {
  const queryParams = {
    companySlug: state.companyVariables.slug,
    learnerSlug: payload.learnerSlug,
  };
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `${payload.assessmentSlug}${base.params(queryParams)}`) };
  const response: base.IResponse<PeerAssessmentResponse> = await base.request(request);

  if (!!response.error) {
    actions.addNotification({
      active: true,
      id: crypto.randomUUID(),
      type: NotificationType.Error,
      priority: NotificationPriority.Toast,
      title: 'Failed to fetch assessment',
      message: response.error.detail,
      toastIncludeIcon: true,
    });
    return;
  }

  return response.data;
}

export async function updatePeerAssessment({ state }: Context, payload: { assessmentSlug: string; peerAssessment: PeerAssessmentRequest }): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, state.companyVariables.slug!, payload.assessmentSlug),
    method: 'POST',
    body: JSON.stringify(payload.peerAssessment),
  };
  await base.request(request);
}

export async function getPeerAssessmentAssigneesManagers({ state }: Context, payload: string): Promise<PeerAssessmentAssigneeManagersResponse[] | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, state.companyVariables.slug!, payload, 'managers') };
  const response: base.IResponse<PeerAssessmentAssigneeManagersResponse[]> = await base.request(request);
  return response.data;
}

export async function getPeerAssessmentAssigneesRoleSkills({ state }: Context, payload: string): Promise<PeerAssessmentAssigneeRoleSkillsResponse[] | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, state.companyVariables.slug!, payload, 'roleskills') };
  const response: base.IResponse<PeerAssessmentAssigneeRoleSkillsResponse[]> = await base.request(request);
  return response.data;
}
